import { useCallback, useState } from "react";
import {
    ProductOption,
    ShippingTimelineDataStore,
    shippingTimelineDataStore,
} from "../../datastore/ShippingTimelineDataStore";
import {
    ResyncResultDisplayValues,
    ShippingTimelineResyncDisplayConverter,
    shippingTimelineResyncDisplayConverter,
} from "./ShippingTimelineResyncDisplayConverter";

/** Intent drives the color of the footer status message (none = neutral hint). */
export type FooterStatusIntent = "none" | "pending" | "danger" | "success";

export interface FooterStatus {
    intent: FooterStatusIntent;
    message: string;
}

export interface ShippingTimelineResyncManager {
    // Product search / selection. The typeahead widget owns its own search
    // interaction; the manager just supplies the query fn and the selection.
    searchProducts: (titleContains: string) => Promise<ProductOption[]>;
    selectedProducts: ProductOption[];
    onAddProduct: (product: ProductOption) => void;
    onRemoveProduct: (productId: string) => void;
    onClearSelection: () => void;

    // Scope description + submit
    scopeDescription: string;
    canSubmit: boolean;

    // Resync request lifecycle
    resyncLoading: boolean;
    resultDisplay: ResyncResultDisplayValues | null;
    errorMessage: string | null;
    onSubmit: () => void;

    // Consolidated status line for the footer action bar (info/error/success).
    footerStatus: FooterStatus;
}

export const useShippingTimelineResyncStateManager = (
    dataStore: ShippingTimelineDataStore = shippingTimelineDataStore,
    converter: ShippingTimelineResyncDisplayConverter = shippingTimelineResyncDisplayConverter,
): ShippingTimelineResyncManager => {
    const [selectedProducts, setSelectedProducts] = useState<ProductOption[]>([]);
    const [resyncLoading, setResyncLoading] = useState<boolean>(false);
    const [result, setResult] = useState<ResyncResultDisplayValues | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const searchProducts = useCallback(
        (titleContains: string) => dataStore.searchProductsByTitle(titleContains),
        [dataStore],
    );

    // The footer result/error describes the LAST resync of a specific product
    // scope. Any change to the selection makes that scope dirty, so we drop the
    // stale result/error and let the footer fall back to the scope hint.
    const invalidateLastRun = useCallback(() => {
        setResult(null);
        setErrorMessage(null);
    }, []);

    const onAddProduct = useCallback((product: ProductOption) => {
        // ProductAutocompleteMultiSelector guarantees uniqueness, so append directly.
        setSelectedProducts((current) => [...current, product]);
        invalidateLastRun();
    }, [invalidateLastRun]);

    const onRemoveProduct = useCallback((productId: string) => {
        setSelectedProducts((current) => current.filter((p) => p.id !== productId));
        invalidateLastRun();
    }, [invalidateLastRun]);

    const onClearSelection = useCallback(() => {
        setSelectedProducts([]);
        invalidateLastRun();
    }, [invalidateLastRun]);

    const onSubmit = useCallback(async () => {
        setResyncLoading(true);
        setErrorMessage(null);
        setResult(null);
        try {
            const raw = await dataStore.resync(selectedProducts.map((p) => p.id));
            setResult(converter.toResultDisplayValues(raw));
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            setErrorMessage(message || "The resync failed. Please try again.");
        } finally {
            setResyncLoading(false);
        }
    }, [dataStore, converter, selectedProducts]);

    const scopeDescription = converter.describeScope(selectedProducts);

    // One status line for the footer action bar, chosen by lifecycle state.
    // Idle shows the scope hint so the footer always tells the user what
    // "Update" will do; the other states echo progress / error / result.
    const footerStatus: FooterStatus = resyncLoading
        ? {
            intent: "pending",
            message: "Recomputation in progress. This can take several seconds to a few minutes. "
                + "Please stay on this page until it finishes.",
        }
        : errorMessage
            ? { intent: "danger", message: errorMessage }
            : result
                ? {
                    intent: "success",
                    message: `Recomputed ${result.lineItemsRecomputedText} across ${result.ordersRecomputedText}`
                        + `${result.backfilledText ? `, ${result.backfilledText}` : ""}. `
                        + `Completed in ${result.elapsedText}.`,
                }
                : { intent: "none", message: scopeDescription };

    return {
        searchProducts,
        selectedProducts,
        onAddProduct,
        onRemoveProduct,
        onClearSelection,
        scopeDescription,
        canSubmit: !resyncLoading,
        resyncLoading,
        resultDisplay: result,
        errorMessage,
        onSubmit: () => { void onSubmit(); },
        footerStatus,
    };
};
